// react
import { createContext, useCallback, useEffect, useMemo, useRef } from 'react';

// react-redux
import { useSelector } from 'react-redux';
import { store } from 'store';
import { setLoggedOut } from 'store/features/userSlice';

// project constants
import { DEFAULT_REFRESH_TOKEN_INTERVAL } from 'constants/auth';

// jwt-decode
import { jwtDecode } from 'jwt-decode';

// cookies
import Cookies from 'universal-cookie';

// project utils
import callAzureFunctionAuth from 'utils-ts/call-azure-function-auth';
import clearReduxStore from 'utils/clear-redux-store';
import { getCookieNameSuffix } from 'utils/cookies';

// dayjs
import dayjs from 'dayjs';
import useLogoutAndRedirect from 'hooks/useLogoutAndRedirect';

const AuthContext = createContext();

const refreshTokenInterval = Number(import.meta.env.VITE_APP_REFRESH_TOKEN_INTERVAL) || DEFAULT_REFRESH_TOKEN_INTERVAL;

export const AuthProvider = ({ children }) => {
  const refreshTimerRef = useRef(null);
  const logoutAndRedirect = useLogoutAndRedirect();

  const userData = useSelector((state) => state.user.data);

  const getAccessTokenData = useCallback(() => {
    try {
      const cookies = new Cookies(null, { path: '/' });
      const cookieNameSuffix = getCookieNameSuffix();
      const accessToken = cookies.get(`AF_ATHP${cookieNameSuffix}`);

      if (!accessToken) return null;

      const decoded = jwtDecode(accessToken);
      if (!decoded?.exp) return null;

      const expDate = dayjs(decoded.exp * 1000);
      const now = dayjs();

      // Added refreshToken interval buffer of 5 minutes to ensure token is refreshed before it actually expires
      const isExpiringSoon = expDate.diff(now, 'minute') <= refreshTokenInterval + 5;

      return { accessToken, decoded, isExpiringSoon };
    } catch (error) {
      console.error('Error reading access token:', error);
      return null;
    }
  }, []);

  const handleAuthError = useCallback(async () => {
    try {
      await callAzureFunctionAuth({
        url: `/auth-remove-access`,
        method: 'get'
      });
    } catch (error) {
      console.error('Error removing access token:', error);
    }

    store.dispatch(setLoggedOut());
    clearReduxStore();
    await logoutAndRedirect();
  }, [logoutAndRedirect]);

  const refreshAccessToken = useCallback(async () => {
    try {
      const tokenData = getAccessTokenData();
      if (!tokenData) {
        throw new Error('No valid access token found');
      }

      // Only refresh token if it is expiring soon
      if (tokenData.isExpiringSoon) {
        await callAzureFunctionAuth({
          url: `/auth-refresh-access`,
          method: 'get'
        });
      }

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      await handleAuthError();
      return false;
    }
  }, [getAccessTokenData, handleAuthError]);

  const contextValue = useMemo(
    () => ({
      refreshAccessToken
    }),
    [refreshAccessToken]
  );

  useEffect(() => {
    if (userData && Object.keys(userData).length > 0) {
      refreshAccessToken();

      refreshTimerRef.current = setInterval(() => {
        refreshAccessToken();
      }, refreshTokenInterval);
    }

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [refreshAccessToken, userData]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
